import { setViewModel } from './applySlice';
import { setState as setAppState } from '../appState/appStateSlice';
import { AnyAction } from '@reduxjs/toolkit';
import { Dispatch } from 'react';
import i18n from '../../i18n/config';

import { NavigateParams } from '../../components/BtNavigate';
import { RootState, store } from '../../app/store';
import { ButtonParams } from '../../components/BtButton';
import { InputParams } from '../../components/BtInput';
import { Company, NoCompany } from '../../components/BtGroup';
import { validateEmail, validateMobile, validateTerms } from '../landing/validators';
import { api, Failure, newApiPost, NewFailure } from '../../support/apiCallHelper';
import { COMPANY, REGISTER } from '../../support/apiConstants';
import { FailedParams } from '../../components/BtFailed';
import { SpinnerParams } from '../../components/BtSpinner';
import { LoadingParams } from '../../components/BtLoading';
import { CheckboxParams } from '../../components/BtCheckbox';
import { useSelector } from 'react-redux';

export function none(): ApplyScreen.None {
  return {
    type: 'none',
    texts: {
      title: '',
      back: '',
      home: '',
      complete: '',
      phone: '',
      email: '',
      accept: '',
      terms: '',
      yes: '',
      apply: '',
      metaTitle: '',
      approvedTitle: '',
      approvedBody: '',
      ok: '',
      agreementNumber: '',
      deniedTitle: '',
      deniedBody: '',
      loading: '',
      noMatch: '',
      supportNumber: '',
      alreadyCustomerTitle: '',
      alreadyCustomerBody: '',
      manualTitle: '',
      manualBody: '',
    },
    state: {
      phone: '',
      customerItems: [null],
      customerId: '',
      email: '',
      agreeToTerms: false,
      canApply: false,
    },
    error: {
      status: '',
      body: '',
    },
    loading: {
      title: '',
      navigation: {
        back: { title: '', image: 'ico_back_arrow', visible: false },
        home: { title: '', image: 'ico_home', visible: false },
        image: '',
      },
    },
  };
}

export namespace ApplyScreen {
  interface Texts {
    title: string;
    back: string;
    home: string;
    complete: string;
    phone: string;
    email: string;
    accept: string;
    terms: string;
    yes: string;
    apply: string;
    metaTitle: string;
    approvedTitle: string;
    approvedBody: string;
    ok: string;
    agreementNumber: string;
    deniedTitle: string;
    deniedBody: string;
    loading: string;
    noMatch: string;
    supportNumber: string;
    alreadyCustomerTitle: string;
    alreadyCustomerBody: string;
    manualTitle: string;
    manualBody: string;
  }

  export interface State {
    phone: string;
    customerItems: [Company | NoCompany | null];
    customerId: string;
    email: string;
    agreeToTerms: boolean;
    canApply: boolean;
  }

  export interface None {
    type: 'none';
    texts: Texts;
    state: State;
    error: FailedParams;
    loading: LoadingParams;
  }

  export interface Loading {
    type: 'loading';
    texts: Texts;
    state: State;
    loading: LoadingParams;
  }

  export interface Ready {
    type: 'ready';
    texts: Texts;
    state: State;
    navigation: NavigateParams;
    greeting: string;
    apply: ButtonParams;
    phone: InputParams;
    email: InputParams;
    customerItems: [Company | NoCompany | null];
    complete: string;
    termsText: string;
    terms: ButtonParams;
    agreeToTerms: CheckboxParams;
    metaTitle: string;
    spinner: SpinnerParams;
    ikon: string;
    supportNumber: string;
  }

  export interface Failed {
    type: 'failed';
    texts: Texts;
    state: State;
    error: FailedParams;
  }

  export interface Finish {
    type: 'finish';
    texts: Texts;
    state: State;
    navigation: NavigateParams;
    title: string;
    ok: ButtonParams;
    body: string;
    bookingId: string;
    id: string;
    image: string;
  }

  export interface Denied {
    type: 'denied';
    texts: Texts;
    state: State;
    navigation: NavigateParams;
    title: string;
    ok: ButtonParams;
    body: string;
    image: string;
    ikon: string;
    supportNumber: string;
  }

  export type ViewModel = None | Loading | Ready | Failed | Finish | Denied;

  export function isNone(viewModel: ViewModel): viewModel is None {
    return (viewModel as None).type === 'none';
  }
  export function isLoading(viewModel: ViewModel): viewModel is Loading {
    return (viewModel as Loading).type === 'loading';
  }
  export function isReady(viewModel: ViewModel): viewModel is Ready {
    return (viewModel as Ready).type === 'ready';
  }
  export function isFailed(viewModel: ViewModel): viewModel is Failed {
    return (viewModel as Failed).type === 'failed';
  }
  export function isFinish(viewModel: ViewModel): viewModel is Finish {
    return (viewModel as Finish).type === 'finish';
  }
  export function isDenied(viewModel: ViewModel): viewModel is Denied {
    return (viewModel as Denied).type === 'denied';
  }

  function getTexts(viewModel: ViewModel): Texts {
    if (isNone(viewModel)) return viewModel.texts;
    if (isLoading(viewModel)) return viewModel.texts;
    if (isReady(viewModel)) return viewModel.texts;
    if (isFailed(viewModel)) return viewModel.texts;
    if (isFinish(viewModel)) return viewModel.texts;
    if (isDenied(viewModel)) return viewModel.texts;
    return {
      title: '',
      back: '',
      home: '',
      complete: '',
      phone: '',
      email: '',
      accept: '',
      terms: '',
      yes: '',
      apply: '',
      metaTitle: '',
      approvedTitle: '',
      approvedBody: '',
      ok: '',
      agreementNumber: '',
      deniedTitle: '',
      deniedBody: '',
      loading: '',
      noMatch: '',
      supportNumber: '',
      alreadyCustomerTitle: '',
      alreadyCustomerBody: '',
      manualTitle: '',
      manualBody: '',
    };
  }

  function loading(viewModel: ViewModel, state: State): Loading {
    const t = i18n.t;
    const logoUrl = useSelector((state: RootState) => state.gatInformation.logoUrl);

    return {
      type: 'loading',
      texts: {
        title: t('applyScreen.title'),
        back: t('applyScreen.back'),
        home: t('applyScreen.home'),
        complete: t('applyScreen.complete'),
        phone: t('applyScreen.phone'),
        email: t('applyScreen.email'),
        accept: t('applyScreen.accept'),
        terms: t('applyScreen.terms'),
        yes: t('applyScreen.yes'),
        apply: t('applyScreen.apply'),
        metaTitle: t('applyScreen.metaTitle'),
        approvedTitle: t('applyScreen.approvedTitle'),
        approvedBody: t('applyScreen.approvedBody'),
        ok: t('applyScreen.ok'),
        agreementNumber: t('applyScreen.agreementNumber'),
        deniedTitle: t('applyScreen.deniedTitle'),
        deniedBody: t('applyScreen.deniedBody'),
        alreadyCustomerTitle: t('applyScreen.alreadyCustomerTitle'),
        alreadyCustomerBody: t('applyScreen.alreadyCustomerBody'),
        loading: t('loading.title'),
        noMatch: t('applyScreen.noMatch'),
        supportNumber: t('applyScreen.supportNumber'),
        manualTitle: t('applyScreen.manualTitle'),
        manualBody: t('applyScreen.manualBody'),
      },
      loading: {
        title: t('loading.title'),
        navigation: {
          back: { title: '', image: 'ico_back_arrow', visible: false },
          home: { title: '', image: 'ico_home', visible: false },
          image: logoUrl,
        },
      },
      state: state,
    };
  }

  function ready(
    viewModel: ViewModel,
    state: State,
    errorPhone: string | null,
    errorEmail: string | null,
    errorAgree: string | null,
  ): Ready {
    const texts = getTexts(viewModel);
    const logoUrl = useSelector((state: RootState) => state.gatInformation.logoUrl);

    return {
      type: 'ready',
      texts: texts,
      greeting: texts.title,
      navigation: {
        back: { title: '', image: 'ico_back_arrow', visible: true },
        home: { title: '', image: 'ico_home', visible: true },
        image: logoUrl,
      },
      state: state,
      apply: { title: texts.apply, image: null, visible: true, disabled: !state.agreeToTerms },
      phone: {
        id: 'phone',
        name: 'phone',
        title: texts.phone,
        message: errorPhone ? errorPhone : '',
        disabled: !state.canApply,
        value: state.phone,
        placeholder: '',
        type: 'tel',
      },
      email: {
        id: 'email',
        name: 'email',
        title: texts.email,
        message: errorEmail ? errorEmail : '',
        disabled: !state.canApply,
        value: state.email,
        placeholder: '',
        type: 'email',
      },
      customerItems: state.customerItems,
      metaTitle: texts.metaTitle,

      complete: texts.complete,
      termsText: texts.accept,
      terms: { title: texts.terms, image: null, visible: true, disabled: !state.canApply },
      agreeToTerms: {
        id: 'agreeToTerms',
        title: texts.yes,
        message: errorAgree ? errorAgree : '',
        value: state.agreeToTerms,
        visible: true,
        disabled: !state.canApply,
      },
      spinner: { interrupt: { title: '', image: '', visible: true } },
      ikon: 'ico_support',
      supportNumber: texts.supportNumber,
    };
  }

  function failed(
    viewModel: ViewModel,
    texts: Texts,
    state: State,
    body: string,
    status: string,
  ): Failed {
    return {
      type: 'failed',
      texts: texts,
      state: state,
      error: {
        status: status,
        body: body,
      },
    };
  }

  function finish(viewModel: ViewModel, texts: Texts, state: State): Finish {
    const logoUrl = useSelector((state: RootState) => state.gatInformation.logoUrl);

    return {
      type: 'finish',
      texts: texts,
      state: state,
      title: texts.approvedTitle,
      navigation: {
        back: { title: '', image: 'ico_back_arrow', visible: false },
        home: { title: '', image: 'ico_home', visible: true },
        image: logoUrl,
      },
      ok: { title: texts.ok, image: null, visible: true },
      body: texts.approvedBody,
      bookingId: texts.agreementNumber,
      id: state.customerId,
      image: 'ico_vehicle_transport_finished',
    };
  }

  function denied(viewModel: ViewModel, texts: Texts, state: State): Denied {
    const logoUrl = useSelector((state: RootState) => state.gatInformation.logoUrl);

    return {
      type: 'denied',
      texts: texts,
      state: state,
      title: texts.deniedTitle,
      navigation: {
        back: { title: '', image: 'ico_back_arrow', visible: false },
        home: { title: '', image: 'ico_home', visible: true },
        image: logoUrl,
      },
      ok: { title: texts.ok, image: null, visible: true },
      body: texts.deniedBody,
      image: 'ico_vehicle_transport_finished',
      ikon: 'ico_support',
      supportNumber: texts.supportNumber,
    };
  }

  export async function reset(): Promise<(dispatch: Dispatch<AnyAction>) => void> {
    return (dispatch) => {
      dispatch(setViewModel(none()));
    };
  }

  export function start(viewModel: ViewModel): (dispatch: Dispatch<AnyAction>) => void {
    return (dispatch) => {
      dispatch(setViewModel(loading(viewModel, viewModel.state)));
    };
  }

  export async function load(
    viewModel: ViewModel,
  ): Promise<(dispatch: Dispatch<AnyAction>) => void> {
    const controller = new AbortController();
    const appState = store.getState().appState.state;
    const customertemsList: [Company | NoCompany | null] = [null];
    let apply = false;

    const callComnpany = await api<CompanyResult>(
      COMPANY,
      appState.createCustomerGovNo.replaceAll('-', ''),
      controller,
    );

    if (callComnpany instanceof Failure) {
      const noCompany: NoCompany = {
        type: 'noCompany',
        icon: 'ico_company_no_info',
        message: viewModel.texts.noMatch,
      };
      customertemsList.push(noCompany);
    } else {
      const comnpany = callComnpany as CompanyResult;

      if (comnpany.name && comnpany.address) {
        const company: Company = {
          type: 'company',
          icon: 'ico_company_info',
          name: comnpany.name,
          street: `${comnpany.address},`,
          zipCode: comnpany.zipCode,
          city: comnpany.city,
        };
        customertemsList.push(company);
        apply = true;
      } else {
        const noCompany: NoCompany = {
          type: 'noCompany',
          icon: 'ico_company_no_info',
          message: viewModel.texts.noMatch,
        };
        customertemsList.push(noCompany);
      }
    }

    const newState: State = {
      ...viewModel.state,
      customerItems: customertemsList,
      canApply: apply,
    };

    return (dispatch) => {
      dispatch(setViewModel(ready(viewModel, newState, null, null, null)));
    };
  }

  export async function uppdateState(
    viewModel: ViewModel,
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<(dispatch: Dispatch<AnyAction>) => void> {
    const target = event.target;

    const value = target.type === 'checkbox' ? target.checked : target.value;
    const newState = { ...viewModel.state, [target.name]: value };
    return async (dispatch) => {
      dispatch(await setViewModel(ready(viewModel, newState, null, null, null)));
    };
  }

  export async function apply(
    viewModel: ViewModel,
  ): Promise<(dispatch: Dispatch<AnyAction>) => void> {
    const errorPhone = validateMobile(viewModel.state.phone);
    const errorEmail = validateEmail(viewModel.state.email);
    const errorAgree = validateTerms(viewModel.state.agreeToTerms);

    if (errorPhone || errorEmail || errorAgree) {
      return async (dispatch) => {
        dispatch(
          await setViewModel(ready(viewModel, viewModel.state, errorPhone, errorEmail, errorAgree)),
        );
      };
    } else {
      const gatResponse = await callGAT(viewModel.state);
      const newState = {
        ...viewModel.state,
        phone: '',
        customerId: '',
        email: '',
        agreeToTerms: false,
        canApply: false,
      };
      return async (dispatch) => {
        if (gatResponse instanceof NewFailure === false) {
          const response = gatResponse as GatResultCustomer;

          // När Kreditupplysning är gjord så ska manuell bort!!!!
          const texts = {
            ...viewModel.texts,
            approvedTitle: viewModel.texts.manualTitle,
            approvedBody: viewModel.texts.manualBody,
            bookingId: '',
          };
          dispatch(
            setAppState({
              ...store.getState().appState.state,
              isActive: 'company',
            }),
          );
          dispatch(await setViewModel(finish(viewModel, texts, newState)));

          //  dispatch(await setViewModel(finish(viewModel, viewModel.texts, { ...viewModel.state, customerId: response.contractCode })));
        } else {
          const resp = gatResponse as NewFailure;
          if (codeCheck(resp.Code) === true && resp.Code === 'ContractCustomerAlreadyRegistered') {
            const texts = {
              ...viewModel.texts,
              deniedTitle: viewModel.texts.alreadyCustomerTitle,
              deniedBody: viewModel.texts.alreadyCustomerBody,
            };
            dispatch(
              setAppState({
                ...store.getState().appState.state,
                isActive: 'company',
              }),
            );
            dispatch(await setViewModel(denied(viewModel, texts, newState)));
          } else {
            // När Kreditupplysning är gjord så kan man bli
            // dispatch(await setViewModel(denied(viewModel, viewModel.texts, viewModel.state)));
            const resp = gatResponse as NewFailure;
            const status = resp.StatusCode === undefined ? 0 : resp.StatusCode;
            const failure = new Failure(status, resp.Message);

            dispatch(
              await setViewModel(
                failed(
                  viewModel,
                  viewModel.texts,
                  viewModel.state,
                  failure.status.toString(),
                  failure.statusText,
                ),
              ),
            );
          }
        }
      };
    }
  }

  function codeCheck(code: string): boolean {
    if (code === 'ContractCustomerAlreadyRegistered') return true;
    else if (code === 'Active') {
      return true;
    } else {
      return false;
    }
  }

  async function callGAT(state: State): Promise<GatResultCustomer | NewFailure> {
    const appState = store.getState().appState.state;
    const formdata = {
      organisationNumber: appState.createCustomerGovNo.replaceAll('-', ''),
      mobileNumber: state.phone.replaceAll('-', ''),
      email: state.email,
    };

    let searchParamsstring = '';
    if (formdata) {
      Object.entries(formdata).forEach(([key, value]) => {
        searchParamsstring += `${key}=${encodeURIComponent(value)}&`;
      });
    }

    searchParamsstring = searchParamsstring.substring(0, searchParamsstring.length - 1);

    try {
      const callGat = await newApiPost<GatResultCustomer | NewFailure>(REGISTER, formdata);
      return callGat;
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.log('GAT ' + e);
      return new NewFailure(0, e.tostring(), '');
    }
  }

  interface CompanyResult {
    name: string;
    address: string;
    zipCode: string;
    city: string;
  }

  interface GatResultCustomer {
    address: string;
    city: string;
    contractCode: string;
    createdAt: string;
    createdBy: string;
    customerCode: string;
    email: string;
    id: number;
    name: string;
    organisationNumber: string;
    phone: string;
    referenceContactPerson: null;
    status: number;
    tariffReference: string;
    updatedAt: string;
    zipCode: string;
  }
}
